import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { FaFileAlt, FaCalendarAlt, FaChevronRight, FaSpinner, FaClock, FaChartPie } from 'react-icons/fa';
import Lottie from 'lottie-react';
import BarChart from './BarChart';
import { getTestResults } from '../lib/api/psychtestApi';

const TestReportModal = ({ isOpen, onClose, report }) => {
  const [animationData, setAnimationData] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  
  // Fetch the Lottie animation data
  useEffect(() => {
    if (isOpen && (report?.isLoading || report?.isProcessing)) {
      fetch('https://db.pokketcoach.com/storage/v1/object/public/public-animations/lottie-animations/pokketthinking_loading.json')
        .then(response => response.json())
        .then(data => {
          setAnimationData(data);
        })
        .catch(error => {
          console.error('Error loading Lottie animation:', error);
        });
    }
  }, [isOpen, report]);

  // Fetch test results if not loading or processing
  useEffect(() => {
    if (isOpen && report?.sessionId && !report.isLoading && !report.isProcessing && !testResults && !loadingResults) {
      const fetchTestResults = async () => {
        setLoadingResults(true);
        try {
          const { data, error } = await getTestResults(report.sessionId);
          if (error) {
            console.error('Error fetching test results:', error);
            return;
          }
          
          if (data) {
            console.log('Test results loaded:', data);
            setTestResults(data);
          }
        } catch (err) {
          console.error('Error in fetching test results:', err);
        } finally {
          setLoadingResults(false);
        }
      };
      
      fetchTestResults();
    }
  }, [isOpen, report, testResults, loadingResults]);
  
  if (!isOpen || !report) return null;

  console.log('Rendering report modal with data:', report);
  
  // Extract report content from various possible API response formats
  const getReportContent = () => {
    // Check different potential properties that might contain the report content
    if (report.reportContent) return report.reportContent;
    if (report.content) return report.content;
    if (report.report) return report.report;
    if (report.markdown) return report.markdown;
    if (report.text) return report.text;
    
    // If there's a data property, check inside that
    if (report.data) {
      const data = report.data;
      if (data.reportContent) return data.reportContent;
      if (data.content) return data.content;
      if (data.report) return data.report;
      if (data.markdown) return data.markdown;
      if (data.text) return data.text;
    }
    
    // If we can't find a specific property, check if report itself is a string
    if (typeof report === 'string') return report;
    
    // Fallback to JSON stringifying the entire report object
    return null;
  };

  const reportContent = getReportContent();
  const isProcessing = report.isProcessing;
  const isLoading = report.isLoading;

  // Components for custom markdown rendering
  const renderers = {
    h1: ({children}) => <h1 className="text-3xl font-bold text-primary mb-4 mt-6 pb-2 border-b border-gray-200">{children}</h1>,
    h2: ({children}) => <h2 className="text-2xl font-semibold text-gray-800 mb-3 mt-5">{children}</h2>,
    h3: ({children}) => <h3 className="text-xl font-medium text-gray-700 mb-2 mt-4">{children}</h3>,
    h4: ({children}) => <h4 className="text-lg font-medium text-gray-700 mb-2 mt-3">{children}</h4>,
    p: ({children}) => <p className="text-gray-600 mb-4 leading-relaxed">{children}</p>,
    ul: ({children}) => <ul className="list-disc pl-5 mb-4 space-y-1 text-gray-600">{children}</ul>,
    ol: ({children}) => <ol className="list-decimal pl-5 mb-4 space-y-1 text-gray-600">{children}</ol>,
    li: ({children}) => <li className="mb-1">{children}</li>,
    blockquote: ({children}) => (
      <blockquote className="border-l-4 border-primary pl-4 py-2 mb-4 bg-blue-50 rounded-r text-gray-700 italic">
        {children}
      </blockquote>
    ),
    a: ({href, children}) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{children}</a>,
    strong: ({children}) => <strong className="font-bold text-gray-800">{children}</strong>,
    em: ({children}) => <em className="italic text-gray-700">{children}</em>,
    code: ({children}) => <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800">{children}</code>,
    pre: ({children}) => <pre className="bg-gray-100 p-4 rounded-lg mb-4 overflow-x-auto font-mono text-sm text-gray-800">{children}</pre>,
    hr: () => <hr className="my-6 border-t border-gray-200" />,
    table: ({children}) => (
      <div className="overflow-x-auto mb-6">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
          {children}
        </table>
      </div>
    ),
    thead: ({children}) => <thead className="bg-gray-50 border-b border-gray-200">{children}</thead>,
    tbody: ({children}) => <tbody className="divide-y divide-gray-200">{children}</tbody>,
    tr: ({children}) => <tr>{children}</tr>,
    th: ({children}) => <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{children}</th>,
    td: ({children}) => <td className="px-4 py-3 text-sm text-gray-600">{children}</td>,
  };

  // Loading state - report is being fetched
  const renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-32 h-32 mb-4">
        {animationData && (
          <Lottie 
            animationData={animationData} 
            loop={true}
            autoplay={true}
          />
        )}
      </div>
      <h3 className="text-xl font-medium text-gray-700 mb-2">Loading Report</h3>
      <p className="text-gray-500 text-center">Your report is currently being processed, check back in a minute or two.</p>
    </div>
  );

  // Processing state - report is being generated
  const renderProcessingState = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="bg-blue-50 rounded-full p-6 mb-6">
        <FaClock className="text-primary text-5xl" />
      </div>
      <h3 className="text-xl font-medium text-gray-800 mb-3">Report In Progress</h3>
      <p className="text-gray-600 text-center max-w-md mb-2">
        Your detailed assessment report is currently being generated. This process may take a few minutes to complete.
      </p>
      <p className="text-gray-500 text-center">
        Please check back in a little while to view your full report.
      </p>
    </div>
  );

  // Render the chart section with the test results
  const renderChartSection = () => {
    if (loadingResults) {
      return (
        <div className="flex justify-center py-8">
          <FaSpinner className="animate-spin text-primary text-2xl" />
        </div>
      );
    }

    if (!testResults || !testResults.categoryResults) {
      return null;
    }

    return (
      <div className="test-report-modal__chart-section bg-gray-50 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-center mb-4">
          <div className="bg-primary bg-opacity-10 p-3 rounded-full mr-3">
            <FaChartPie className="text-primary text-xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Assessment Results Overview</h3>
        </div>
        <p className="text-gray-600 text-center mb-6">
          This section contains visual representation of your scores across different categories. The full report continues below.
        </p>
        
        <div className="py-4">
          <BarChart categoryResults={testResults.categoryResults} />
        </div>
      </div>
    );
  };

  return (
    <div className="test-report-modal fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="test-report-modal__content bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
        {/* Decorative element */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-primary to-purple-400 rounded-t-xl"></div>
        
        {/* Header with gradient background */}
        <div className="test-report-modal__header px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="test-report-modal__icon-wrapper bg-primary bg-opacity-10 p-3 rounded-full mr-4">
                <FaFileAlt className="text-primary text-xl" />
              </div>
              <h2 className="test-report-modal__title text-2xl font-bold text-gray-800">{report.testName || 'Test Report'}</h2>
            </div>
            <button 
              className="test-report-modal__close-button text-gray-400 hover:text-gray-600 transition-colors"
              onClick={onClose}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="test-report-modal__body p-6">
          {/* Report content with custom styling */}
          <div className="test-report-modal__content rounded-lg border border-gray-100 p-6 bg-white shadow-sm">
            {isLoading ? renderLoadingState() : isProcessing ? renderProcessingState() : (
              <>
                {/* Chart Section */}
                {renderChartSection()}
                
                <div className="prose prose-sm sm:prose max-w-none prose-headings:text-primary prose-headings:font-semibold prose-blockquote:border-primary prose-blockquote:border-l-4 lg:columns-1 xl:columns-auto xl:column-gap-12">
                  <ReactMarkdown components={renderers}>{reportContent}</ReactMarkdown>
                </div>
                
                <div className="test-report-modal__content-footer border-t border-gray-200 pt-4 mt-6 text-sm text-gray-500 italic">
                  <p>This report was automatically generated based on a combination of your conversations with Pokket and your test results.</p>
                </div>
              </>
            )}
          </div>
        </div>
        
        <div className="test-report-modal__footer px-6 py-4 border-t border-gray-200 flex justify-end">
          <button 
            className="test-report-modal__close-button flex items-center px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors"
            onClick={onClose}
          >
            Close Report <FaChevronRight className="ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestReportModal; 